// Browser STT + Premium TTS (ElevenLabs via backend, fallback to browser).
const BACKEND_URL = (typeof process !== 'undefined' ? process.env.REACT_APP_BACKEND_URL : '') || '';

export const isSTTSupported = () => {
  return typeof window !== 'undefined' && (
    !!window.SpeechRecognition || !!window.webkitSpeechRecognition
  );
};

export const isTTSSupported = () => {
  return typeof window !== 'undefined' && !!window.speechSynthesis;
};

export const startSTT = ({ lang = 'fr-FR', onResult, onError, onEnd, continuous = false } = {}) => {
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Ctor) {
    onError?.(new Error('Reconnaissance vocale non supportée par ce navigateur'));
    return { stop: () => {}, abort: () => {} };
  }
  const rec = new Ctor();
  rec.lang = lang;
  rec.interimResults = true;
  rec.continuous = continuous;
  rec.maxAlternatives = 1;

  rec.onresult = (event) => {
    let interim = '';
    let final = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const res = event.results[i];
      if (res.isFinal) final += res[0].transcript;
      else interim += res[0].transcript;
    }
    if (final) onResult?.({ transcript: final, isFinal: true });
    else if (interim) onResult?.({ transcript: interim, isFinal: false });
  };
  rec.onerror = (e) => onError?.(new Error(e.error || 'STT error'));
  rec.onend = () => onEnd?.();

  try { rec.start(); } catch (e) { onError?.(e); }
  return {
    stop: () => { try { rec.stop(); } catch (err) { console.debug('YNX STT: stop already done', err); } },
    abort: () => { try { rec.abort(); } catch (err) { console.debug('YNX STT: abort already done', err); } },
  };
};

let _currentAudio = null;
let _currentUtterance = null;

const pickVoice = (lang = 'fr-FR', preferredName = null) => {
  if (!isTTSSupported()) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (preferredName) {
    const v = voices.find((x) => x.name.toLowerCase().includes(preferredName.toLowerCase()));
    if (v) return v;
  }
  const fr = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith('fr'));
  if (fr.length > 0) {
    const male = fr.find((v) => /thomas|henri|paul|male|man/i.test(v.name));
    return male || fr[0];
  }
  return voices[0] || null;
};

export const stopSpeaking = () => {
  try {
    if (_currentAudio) {
      _currentAudio.pause();
      _currentAudio = null;
    }
    if (isTTSSupported()) {
      window.speechSynthesis.cancel();
    }
    _currentUtterance = null;
  } catch (err) { console.debug('YNX TTS: stopSpeaking cleanup', err); }
};

/**
 * Speak text. Tries premium ElevenLabs first (via backend), then falls
 * back to free browser TTS on any error / unavailability.
 */
export const speak = async ({ text, lang = 'fr-FR', voice = null, voiceId = null, rate = 1.0, pitch = 1.0, onEnd, onError } = {}) => {
  if (!text || !text.trim()) return { stop: () => {} };
  stopSpeaking();

  // 1) Premium: ElevenLabs
  if (BACKEND_URL) {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/tts/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.slice(0, 4000),
          voice_id: voiceId || 'EXAVITQu4vr4xnSDxMaL',
        }),
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.playbackRate = Math.max(0.5, Math.min(2, rate));
        audio.onended = () => { URL.revokeObjectURL(url); _currentAudio = null; onEnd?.(); };
        audio.onerror = (e) => { URL.revokeObjectURL(url); _currentAudio = null; onError?.(e); };
        _currentAudio = audio;
        await audio.play();
        return { stop: stopSpeaking };
      }
      console.warn('YNX: ElevenLabs TTS HTTP', resp.status, '→ browser fallback');
    } catch (err) {
      console.warn('YNX: ElevenLabs TTS failed, falling back to browser', err);
    }
  }

  // 2) Fallback: browser
  if (!isTTSSupported()) {
    onError?.(new Error('Synthèse vocale non supportée'));
    return { stop: () => {} };
  }
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;
    u.rate = rate;
    u.pitch = pitch;
    const v = pickVoice(lang, voice);
    if (v) u.voice = v;
    u.onend = () => { _currentUtterance = null; onEnd?.(); };
    u.onerror = (e) => { _currentUtterance = null; onError?.(e); };
    _currentUtterance = u;
    window.speechSynthesis.speak(u);
  } catch (e) {
    onError?.(e);
  }
  return { stop: stopSpeaking };
};

export const isSpeaking = () => {
  if (_currentAudio && !_currentAudio.paused) return true;
  return isTTSSupported() && window.speechSynthesis.speaking;
};
